<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title>
        <router-link
          class="text-decoration--none"
          :to="{
            name: $route.name.includes('training.resources')
              ? 'training.resources'
              : 'resources'
          }"
        >
          Edit
          {{
            $route.name.includes('training.resources')
              ? 'Student Leader Training Resources'
              : 'Lunchtime Group Resources'
          }}
        </router-link>
        <template v-if="resource">
          <v-icon> {{ icons.arrow }} </v-icon>
          <span>{{ resource.title }}</span>
        </template>
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <div class="ma-4" v-if="resource">
      <resource-form ref="form" tag v-model="resource" />
      <div class="d-flex mt-5">
        <h2>
          Resource Videos<v-btn @click="addVideo" :icon="true" color="primary">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </h2>
      </div>
      <div class="d-flex mt-5">
        <v-btn
          color="primary"
          @click="setOrder"
          :loading="settingOrder"
          v-if="resource.videos && resource.videos.length"
        >
          Save Order
        </v-btn>
      </div>

      <template v-if="resource.videos && resource.videos.length">
        <draggable :list="resource.videos" class="row my-5" :sort="true">
          <v-col
            v-for="(video, index) in resource.videos"
            :key="index"
            cols="12"
            md="4"
          >
            <video-card
              @remove="onRemove($event, index)"
              @edit="onEdit"
              :video="video"
            />
          </v-col>
        </draggable>
      </template>
      <div v-else class="text-center subtitle-1 grey--text">
        No videos added
      </div>

      <v-divider class="my-8" />

      <v-btn @click="submit" color="primary">Submit and save</v-btn>
    </div>

    <resource-video-form ref="videoForm" @saved="onVideoSave" />
  </div>
</template>

<script>
import { mdiChevronRight } from '@mdi/js'
import Resource from '@/models/Resource'
import ResourceForm from '@/components/resource/Form'
import ResourceVideoForm from '@/components/resource/VideoForm'
import VideoCard from '@/components/resource/VideoCard'
import ResourceVideo from '@/models/ResourceVideo'
import { map, findIndex } from 'lodash'
import * as ResourceType from '@/utils/consts/ResourceType'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import draggable from 'vuedraggable'

export default {
  name: 'EditResources',

  components: {
    ResourceForm,
    ResourceVideoForm,
    VideoCard,
    AppBarNavIcon,
    draggable
  },

  data() {
    return {
      icons: {
        arrow: mdiChevronRight
      },
      loading: false,
      settingOrder: false,
      resource: new Resource({
        title: '',
        subtitle: ''
      }),
      editing: false
    }
  },

  mounted() {
    this.getResource()
  },

  methods: {
    getResource() {
      Resource.include('cover', 'videos.cover', 'videos.attachments', 'tag')
        .where(
          'type',
          this.$route.name.includes('training.resources')
            ? ResourceType.TRAINING
            : ResourceType.LTG
        )
        .$find(this.$route.params.id)
        .then(resource => {
          this.resource = resource
          this.resource.videos = map(resource.videos, video => {
            return new ResourceVideo(video)
          })
        })
        .catch(() => {
          this.$router.replace({ name: 'resources' })
        })
    },

    onVideoSave(video) {
      const videoIndex = findIndex(this.resource.videos, { id: video.id })
      if (videoIndex === -1) {
        this.resource.videos.push(video)
      } else {
        this.$set(this.resource.videos, videoIndex, video)
      }
    },

    onEdit(video) {
      this.editing = true
      this.$refs.videoForm.open({
        resource_id: this.resource.id,
        ...video
      })
    },

    onRemove(video, index) {
      this.$confirm(
        `Are you sure you want to remove this resource video "${video.title}"?`,
        {
          color: 'primary',
          title: 'Remove video resource'
        }
      ).then(confirm => {
        if (confirm) {
          video
            .for(this.resource)
            .delete()
            .then(() => {
              this.$delete(this.resource.videos, index)
            })
        }
      })
    },

    addVideo() {
      this.editing = false
      this.$refs.videoForm.open(
        new ResourceVideo({
          resource_id: this.resource.id,
          title: '',
          description: '',
          tips: '',
          notes: ''
        })
      )
    },

    submit() {
      this.loading = true
      const form = new FormData()
      form.append('_method', 'PUT')
      form.append('title', this.resource.title)
      form.append('subtitle', this.resource.subtitle)
      form.append('resource_category_id', this.resource.resource_category_id)
      if (this.resource.cover instanceof Blob) {
        form.append('cover', this.resource.cover)
      }

      this.$api
        .post(`/resources/${this.resource.id}`, form)
        .then(() => {
          this.$toast('Lunchtime group resource successfully saved!', {
            color: 'green'
          })
          this.$router.replace({
            name: this.$route.name.includes('training')
              ? 'training.resources'
              : 'resources'
          })
        })
        .catch(({ response }) => {
          if (response.data && response.data.errors) {
            this.$refs.form.setErrors(response.data.errors)
          }
        })
        .then(() => {
          this.loading = false
        })
    },

    setOrder() {
      this.settingOrder = true

      this.$api
        .post(`/resources/${this.resource.id}/videos/set-order`, {
          resources_videos: this.resource.videos
        })
        .then(response => {
          this.showSnackbar(response.data.message)
        })
        .catch(() => {
          this.$toast('Resource videos order successfully changed!', {
            color: 'green'
          })
        })
        .finally(() => (this.settingOrder = false))
    }
  }
}
</script>
